import { createFileRoute } from '@tanstack/react-router';
import { useMember } from '@/lib/use-session';
import { EventsListBody } from '@/components/events/EventsListBody';
import {
  PublicEventsListBody,
  parsePublicEventsSearch,
  type PublicEventsSearchInput,
} from '@/components/events/public/index';
import {
  type EventsListFormat,
  type EventsListTab,
  type EventsSearchInput,
} from '@/lib/queries/events';

function parseTab(v: unknown): EventsListTab | undefined {
  return v === 'mine' || v === 'circle' || v === 'network' || v === 'platform'
    ? v
    : undefined;
}
function parseFormat(v: unknown): EventsListFormat | 'any' | undefined {
  if (v === 'in_person' || v === 'virtual' || v === 'any') return v;
  return undefined;
}
function pickStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

// One URL serves both lists: members get the tabbed Events page, signed-out
// visitors the public directory. The search keeps both vocabularies so a
// link survives signing in or out (`q` and `format` mean the same on both).
type EventsRouteSearch = EventsSearchInput & PublicEventsSearchInput;

export const Route = createFileRoute('/_public/events/')({
  validateSearch: (raw: Record<string, unknown>): EventsRouteSearch => ({
    ...(pickStr(raw.q) ? { q: pickStr(raw.q) as string } : {}),
    ...(parseTab(raw.tab) !== undefined
      ? { tab: parseTab(raw.tab) as EventsListTab }
      : {}),
    ...(raw.when === 'past' || raw.when === 'upcoming'
      ? { when: raw.when }
      : {}),
    ...(pickStr(raw.network) ? { network: pickStr(raw.network) as string } : {}),
    ...(parseFormat(raw.format) !== undefined
      ? { format: parseFormat(raw.format) as EventsListFormat | 'any' }
      : {}),
    ...(pickStr(raw.from) ? { from: pickStr(raw.from) as string } : {}),
    ...(pickStr(raw.to) ? { to: pickStr(raw.to) as string } : {}),
    ...parsePublicEventsSearch(raw),
  }),
  component: EventsListPage,
});

function EventsListPage() {
  const member = useMember();
  return member !== null ? <EventsListBody /> : <PublicEventsListBody />;
}
